import React, { useEffect, useMemo, useState } from 'react';
import { FiX, FiCheckCircle, FiPlus, FiList } from 'react-icons/fi';
import { QuoteForm } from './QuoteForm';
import { QuoteShareActions } from './QuoteShareActions';
import { quoteEndpoints, inr, fmtDate } from './quoteApi';

const QuoteSuccess = ({ quote, isAdmin, onNew }) => (
  <div data-testid="quote-success" className="max-w-xl mx-auto text-center space-y-5 py-4">
    <FiCheckCircle className="w-12 h-12 text-green-600 mx-auto" />
    <div>
      <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#F57C20]">Quotation ready</p>
      <h3 data-testid="quote-success-no" className="text-2xl text-[#1A1A2E] mt-1" style={{ fontFamily: "'Playfair Display', serif" }}>{quote.quote_no}</h3>
      <p className="text-sm text-[#6B7280] mt-1">
        {quote.data.villa.bhk_name} · {fmtDate(quote.data.check_in)} → {fmtDate(quote.data.check_out)} · {quote.data.guests} guests
      </p>
      <p data-testid="quote-success-total" className="text-3xl font-bold text-[#1A1A2E] mt-3">{inr(quote.data.grand_total)}</p>
      <p className="text-[11px] text-[#6B7280]">incl. refundable deposit · valid until {fmtDate(quote.data.valid_until)}</p>
    </div>
    <div className="text-left"><QuoteShareActions quote={quote} waPhone={isAdmin ? quote.data.client_phone : ''} /></div>
    {!isAdmin && <p className="text-xs text-[#6B7280]">Our team has received your request and will reach out shortly. Questions? WhatsApp +91 98200 88292.</p>}
    <button data-testid="quote-new-btn" onClick={onNew} className="text-sm text-[#F57C20] font-semibold hover:underline">+ Create another quote</button>
  </div>
);

const RecentQuotes = ({ ep, onOpen }) => {
  const [items, setItems] = useState(null);
  useEffect(() => { ep.list().then((r) => setItems(r.data.quotations || [])).catch(() => setItems([])); }, [ep]);
  if (!items) return <p className="text-sm text-[#6B7280]">Loading…</p>;
  if (!items.length) return <p data-testid="quote-recent-empty" className="text-sm text-[#6B7280]">No quotations yet.</p>;
  return (
    <div data-testid="quote-recent-list" className="divide-y divide-gray-100">
      {items.map((q) => (
        <button key={q.id} data-testid={`quote-recent-${q.quote_no}`} onClick={() => onOpen(q)} className="w-full text-left py-3 flex justify-between gap-3 hover:bg-[#FFF7ED] px-2 rounded transition-colors">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[#1A1A2E]">{q.quote_no} · {q.data.client_name}
              <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-full uppercase ${q.source === 'guest' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{q.source === 'guest' ? 'Guest lead' : 'Admin'}</span>
            </p>
            <p className="text-[11px] text-[#6B7280] truncate">{q.data.villa.bhk_name} · {fmtDate(q.data.check_in)} → {fmtDate(q.data.check_out)} · {q.data.guests} guests{q.data.client_phone ? ` · ${q.data.client_phone}` : ''}</p>
          </div>
          <span className="text-sm font-bold text-[#1A1A2E] flex-shrink-0">{inr(q.data.grand_total)}</span>
        </button>
      ))}
    </div>
  );
};

export const QuoteModal = ({ open, onClose, mode = 'guest', token, fixedVillaId, initial, title }) => {
  const isAdmin = mode !== 'guest';
  const ep = useMemo(() => quoteEndpoints(mode, token), [mode, token]);
  const [quote, setQuote] = useState(null);
  const [tab, setTab] = useState('new');
  const [formKey, setFormKey] = useState(0);
  useEffect(() => { if (!open) { setQuote(null); setTab('new'); } }, [open]);
  if (!open) return null;
  const reset = () => { setQuote(null); setTab('new'); setFormKey((k) => k + 1); };
  const tabBtn = (id, Icon, label) => (
    <button data-testid={`quote-tab-${id}`} onClick={() => { setTab(id); setQuote(null); }}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${tab === id ? 'bg-[#1A1A2E] text-white' : 'bg-gray-100 text-[#374151] hover:bg-gray-200'}`}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
  return (
    <div data-testid="quote-modal" className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-5xl max-h-[94vh] sm:max-h-[90vh] rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 px-5 md:px-6 py-4 border-b border-gray-100">
          <div className="min-w-0">
            <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#F57C20] truncate">Royal Garden Villas · Instant Quote</p>
            <h2 className="text-lg md:text-xl text-[#1A1A2E] truncate" style={{ fontFamily: "'Playfair Display', serif" }}>{title || 'Get Your Quotation'}</h2>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button data-testid="quote-modal-close" onClick={onClose} aria-label="Close" className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center"><FiX className="w-5 h-5" /></button>
          </div>
        </div>
        {isAdmin && <div className="flex gap-2 px-5 md:px-6 pt-3">{tabBtn('new', FiPlus, 'New')}{tabBtn('recent', FiList, 'Recent')}</div>}
        <div className="overflow-y-auto px-5 md:px-6 py-5">
          {quote ? <QuoteSuccess quote={quote} isAdmin={isAdmin} onNew={reset} />
            : tab === 'recent' ? <RecentQuotes ep={ep} onOpen={setQuote} />
              : <QuoteForm key={formKey} mode={mode} token={token} fixedVillaId={fixedVillaId} initial={initial} onCreated={setQuote} />}
        </div>
      </div>
    </div>
  );
};

export default QuoteModal;
